/* eslint-disable indent */
import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { useSnackbar } from 'notistack'
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Grid,
  Switch,
  TextField
} from '@mui/material'
import { createStyles, makeStyles } from '@mui/styles'

import { updateFlowCondition, cacheUpdateFlowGroupCondition } from '../../../../../../actions'
import { textFieldTypes, variantTypes, userRightTypes } from '../../../../../../constants'
import { Logger, LoggerTypes, checkUpdateResponse, checkErrorResponse } from '../../../../../../utils'
import { FormTitle, iconNames } from '../../../../../../components'
import { FlowElementBottomButtons } from '../../../FlowElementBottomButtons'
import { checkPermissions } from '../../../../../../components/AccessControl'
import '../../../common-elements-style.scss'
import './style.scss'

const defaultValue = {
  name: '',
  description: '',
  next_action_false_is_wait: false,
  next_action_true_is_wait: false,
  check_weather_on_true: false,
  check_weather_on_false: false,
  invert_true_false_actions: false
}

const useStyles = makeStyles((theme) =>
  createStyles({
    headerColor: {
      color: theme.palette.secondary.main,
      opacity: '0.6'
    }
  })
)

export const FlowElementConditionAboutPanel = ({
  currentProject,
  currentScenario,
  currentFlowGroup,
  currentCondition,
  refreshAfterSave
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const { enqueueSnackbar } = useSnackbar()
  const styleClasses = useStyles()

  const nameValue = `${currentCondition?.name ? currentCondition?.name : ''}` || defaultValue.name
  const descriptionValue =
    `${currentCondition?.description ? currentCondition?.description : ''}` || defaultValue.description
  const next_action_true_is_waitValue =
    currentCondition?.next_action_true_is_wait || defaultValue.next_action_true_is_wait
  const next_action_false_is_waitValue =
    currentCondition?.next_action_false_is_wait || defaultValue.next_action_false_is_wait
  const check_weather_on_trueValue = currentCondition?.check_weather_on_true || defaultValue.check_weather_on_true
  const check_weather_on_falseValue = currentCondition?.check_weather_on_false || defaultValue.check_weather_on_false

  const [name, setName] = useState(nameValue)
  const [description, setDescription] = useState(descriptionValue)
  const [next_action_true_is_wait, setNextActionTrueIsWait] = useState(next_action_true_is_waitValue)
  const [next_action_false_is_wait, setNextActionFalseIsWait] = useState(next_action_false_is_waitValue)
  const [check_weather_on_true, setCheckWeatherOnTrue] = useState(check_weather_on_trueValue)
  const [check_weather_on_false, setCheckWeatherOnFalse] = useState(check_weather_on_falseValue)
  const [invert_true_false_actions, setInvertTrueFalseActions] = useState(defaultValue.invert_true_false_actions)

  const [errors, setErrors] = useState(null)

  useEffect(() => {
    setName(nameValue)
    setDescription(descriptionValue)
    setNextActionTrueIsWait(next_action_true_is_waitValue)
    setNextActionFalseIsWait(next_action_false_is_waitValue)
    setCheckWeatherOnTrue(check_weather_on_trueValue)
    setCheckWeatherOnFalse(check_weather_on_falseValue)
  }, [currentCondition])

  const onClickSave = (e, isInvert) => {
    e.stopPropagation()

    const pid = currentProject?.id || null
    const sid = currentScenario?.id || null
    const fid = currentFlowGroup?.id || null

    const newCondition = {
      id: currentCondition?.id || null,
      // -- about
      name: name === '' ? null : name,
      description: description === '' ? null : description,
      // -- waiting
      next_action_false_is_wait: Boolean(next_action_false_is_wait),
      next_action_true_is_wait: Boolean(next_action_true_is_wait),
      // -- check weather
      check_weather_on_true: Boolean(check_weather_on_true),
      check_weather_on_false: Boolean(check_weather_on_false),
      // -- invert
      invert_true_false_actions: Boolean(isInvert)
    }

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionAboutPanel.onClickSave()', { newCondition, isInvert })

    dispatch(updateFlowCondition(pid, sid, fid, newCondition))
      .then(async (res) => {
        Logger.trace(
          LoggerTypes.CONTROLLER,
          'FlowElementConditionAboutPanel.onClickSave().dispatch(updateFlowCondition(...)).then()',
          { res }
        )
        checkUpdateResponse(res, enqueueSnackbar, t)

        // -- remove the previous errors
        await setErrors(null)

        // Now the task has been updated in the back-end we propagate the change in the REDUX state cache
        Logger.trace(
          LoggerTypes.CONTROLLER,
          'FlowElementConditionAboutPanel.onClickSave().cacheUpdateFlowGroupCondition()'
        )
        dispatch(cacheUpdateFlowGroupCondition(currentFlowGroup, res.data))

        // -- Update the Condition Element
        if (refreshAfterSave && typeof refreshAfterSave === 'function') {
          const conditionUpdated = {
            ...res.data,
            inverted: isInvert
          }
          refreshAfterSave(currentFlowGroup, [conditionUpdated])
        }

        await setInvertTrueFalseActions(false)
      })
      .catch((res) => {
        Logger.trace(
          LoggerTypes.WARN,
          'FlowElementConditionAboutPanel.onClickSave().dispatch(updateFlowCondition(...)).catch()',
          { res }
        )

        checkErrorResponse(res, enqueueSnackbar, t)

        if (res?.response?.data?.fields) {
          setErrors(res.response.data.fields)
        }
      })
  }

  // --

  const onChangeName = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionAboutPanel.onChangeTitle()', { name: _value })
    setName(_value)
  }

  const onChangeDescription = (e) => {
    e.stopPropagation()
    const _value = e.target.value
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionAboutPanel.onChangeDescription()', {
      description: _value
    })
    setDescription(_value)
  }

  const onWaitingYesChange = (e) => {
    e.stopPropagation()
    const new_next_action_true_is_wait = !next_action_true_is_wait

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionAboutPanel.onWaitingYesChange()', {
      next_action_true_is_wait: new_next_action_true_is_wait
    })

    setNextActionTrueIsWait(new_next_action_true_is_wait)

    if (new_next_action_true_is_wait && next_action_false_is_wait) {
      setNextActionFalseIsWait(false)
    }
  }

  const onWaitingNoChange = (e) => {
    e.stopPropagation()
    const new_next_action_false_is_wait = !next_action_false_is_wait

    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionAboutPanel.onWaitingNoChange()', {
      next_action_false_is_wait: new_next_action_false_is_wait
    })

    setNextActionFalseIsWait(new_next_action_false_is_wait)

    if (new_next_action_false_is_wait && next_action_true_is_wait) {
      setNextActionTrueIsWait(false)
    }
  }

  const onCheckWeatherTrueChange = (e) => {
    e.stopPropagation()
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionAboutPanel.onCheckWeatherTrueChange()', {
      check_weather_on_true: !check_weather_on_true
    })
    setCheckWeatherOnTrue(!check_weather_on_true)
  }

  const onCheckWeatherFalseChange = (e) => {
    e.stopPropagation()
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionAboutPanel.onCheckWeatherFalseChange()', {
      check_weather_on_false: !check_weather_on_false
    })
    setCheckWeatherOnFalse(!check_weather_on_false)
  }

  const onInvertChange = async (e) => {
    e.stopPropagation()
    Logger.trace(LoggerTypes.CONTROLLER, 'FlowElementConditionAboutPanel.onInvertChange()', {
      invert_true_false_actions: !invert_true_false_actions
    })
    await setInvertTrueFalseActions(!invert_true_false_actions)

    onClickSave(e, true)
  }

  Logger.trace(LoggerTypes.CONTAINER, 'FlowElementConditionAboutPanel.render()', {
    currentCondition,
    about: {
      name,
      description,
      next_action_false_is_wait,
      next_action_true_is_wait,
      check_weather_on_true,
      check_weather_on_false,
      invert_true_false_actions
    },
    errors
  })

  return (
    <div className="FlowElementConditionAboutPanel">
      <form>
        {/* == ABOUT == */}
        <Grid container spacing={2} style={{ padding: '30px 10px 20px' }}>
          {/*  name  */}
          <Grid item xs={12} sm={12}>
            <TextField
              type={textFieldTypes.TEXT}
              name="name"
              id="name"
              value={name}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.conditions.panels.about.name.label')}
              className="common_fullwidth"
              onChange={onChangeName}
              placeholder={t('flowActions.conditions.panels.about.name.placeholder')}
              error={Boolean(errors && errors?.name)}
              helperText={(errors && errors?.name) || ''}
            />
          </Grid>

          {/*  description  */}
          <Grid item xs={12} sm={12} className="common_fullwidth" style={{ marginTop: '15px' }}>
            <TextField
              type={textFieldTypes.TEXT}
              name="description"
              id="description"
              value={description}
              variant={variantTypes.OUTLINED}
              label={t('flowActions.conditions.panels.about.description.label')}
              className="common_fullwidth"
              onChange={onChangeDescription}
              placeholder={t('flowActions.conditions.panels.about.description.placeholder')}
              error={Boolean(errors && errors?.description)}
              helperText={(errors && errors?.description) || ''}
            />
          </Grid>
        </Grid>

        {/* == WAITING == */}
        <FormTitle
          className={`parameterGroup ${styleClasses.headerColor}`}
          title={t('flowActions.conditions.panels.waiting.title')}
          icon={iconNames.CLOCK}
          iconSize={16}
        />
        <Grid container spacing={2} style={{ padding: '0 10px 20px' }}>
          {/*  if 'True'  */}
          <Grid item xs={12} sm={12}>
            <FormControl error={Boolean(errors && errors?.next_action_true_is_wait)} className="common_fullwidth">
              <FormControlLabel
                label={t('flowActions.conditions.panels.waiting.yes_label')}
                control={<Checkbox checked={next_action_true_is_wait} onChange={onWaitingYesChange} />}
              />
              <FormHelperText>{(errors && errors?.next_action_true_is_wait) || ''}</FormHelperText>
            </FormControl>
          </Grid>
          {/*  if 'False'  */}
          <Grid item xs={12} sm={12}>
            <FormControl error={Boolean(errors && errors?.next_action_false_is_wait)} className="common_fullwidth">
              <FormControlLabel
                label={t('flowActions.conditions.panels.waiting.no_label')}
                control={<Checkbox checked={next_action_false_is_wait} onChange={onWaitingNoChange} />}
              />
              <FormHelperText>{(errors && errors?.next_action_false_is_wait) || ''}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        {/* == CHECK WEATHER == */}
        <FormTitle
          className={`parameterGroup ${styleClasses.headerColor}`}
          title={t('flowActions.conditions.panels.check_weather.title')}
          icon={iconNames.WEATHER_DATA}
          iconSize={16}
        />
        <Grid container spacing={2} style={{ padding: '0 10px 20px' }}>
          {/*  if 'True'  */}
          <Grid item xs={12} sm={12}>
            <FormControl error={Boolean(errors && errors?.check_weather_on_true)} className="common_fullwidth">
              <FormControlLabel
                label={t('flowActions.conditions.panels.check_weather.yes_label')}
                control={<Checkbox checked={check_weather_on_true} onChange={onCheckWeatherTrueChange} />}
              />
              <FormHelperText>{(errors && errors?.check_weather_on_true) || ''}</FormHelperText>
            </FormControl>
          </Grid>
          {/*  if 'False'  */}
          <Grid item xs={12} sm={12}>
            <FormControl error={Boolean(errors && errors?.check_weather_on_false)} className="common_fullwidth">
              <FormControlLabel
                label={t('flowActions.conditions.panels.check_weather.no_label')}
                control={<Checkbox checked={check_weather_on_false} onChange={onCheckWeatherFalseChange} />}
              />
              <FormHelperText>{(errors && errors?.check_weather_on_false) || ''}</FormHelperText>
            </FormControl>
          </Grid>
        </Grid>

        {/* == INVERT == */}
        <FormTitle
          className={`parameterGroup ${styleClasses.headerColor}`}
          title={t('flowActions.conditions.panels.invert.title')}
          icon={iconNames.MOVEMENT}
          iconSize={16}
        />
        <Grid container spacing={2} style={{ padding: '0 10px 20px' }}>
          {/*  invert 'True' <-> 'False'  */}
          <Grid item xs={12} sm={12} className="common_fullwidth">
            <p className="explanation">{t('flowActions.conditions.panels.invert.explanation')}</p>
            <hr />
            <FormGroup>
              <FormControlLabel
                disabled={!checkPermissions([userRightTypes.SCENARIO_ATTRIBUTES_EDIT])}
                label={t('flowActions.conditions.panels.invert.label')}
                control={<Switch checked={invert_true_false_actions} onChange={onInvertChange} />}
              />
            </FormGroup>
          </Grid>
        </Grid>
        <FlowElementBottomButtons onSave={onClickSave} />
      </form>
    </div>
  )
}

FlowElementConditionAboutPanel.displayName = 'FlowElementConditionAboutPanel'

FlowElementConditionAboutPanel.propTypes = {
  currentProject: PropTypes.object.isRequired,
  currentScenario: PropTypes.object.isRequired,
  currentFlowGroup: PropTypes.object.isRequired,
  currentCondition: PropTypes.object,
  // -- Flow's Action
  conditions: PropTypes.arrayOf(PropTypes.object),
  // -- refresh after saving
  refreshAfterSave: PropTypes.func
}

FlowElementConditionAboutPanel.defaultProps = {
  currentProject: null,
  currentScenario: null,
  currentFlowGroup: null,
  currentCondition: null,
  // -- Flow's Action
  conditions: [],
  // -- refresh after saving
  refreshAfterSave: null
}
